<!--
  Model read page for `service/:serviceKey/dev-model/:modelKey/:modelId`.

  Moved here from views/service/TabulatorPages/ as part of the Tabulator ->
  v-data-table migration; the route NAME is unchanged
  (PageServiceModelBreadTabulatorRead), so the ~15 call sites that push to it —
  including BreadTabulatorActionButton — are untouched.

  What changed against the page it replaces:

    1. Related-record tabs render BreadDataTable instead of BreadTabulator, via
       BreadDataTableTabChild (which collapses the old TabChildV2 /
       TabIntermediate pair — they were the same component twice).
    2. The Info tab is a BreadDataRecordView definition grid rather than a
       two-column <v-simple-table>, and it now leads the tab strip: on a read
       page the record itself is the subject, its relations are context.
    3. The page chrome follows DataTablePages/PageServiceModelBrowse.vue — a
       toolbar carrying the record identity and its primary action, then the
       tabbed panel.
    4. Only the active tab is mounted. v-tabs-items kept every tab alive, so
       opening a user fired a browse request per relation before you looked at
       any of them.
    5. Editing happens in a dialog over the record instead of on a page of its
       own. Same BreadModelForm, same update URL, same payload — what goes is
       the navigation round-trip and the loss of context that came with it. The
       /item/edit route still resolves, for links that point straight at it.
    6. The tab strip carries counts, so "does this user have any roles?" is
       answered before you click. The number is the record's own `_count` where
       the read endpoint supplies one, and the tab's reported total otherwise.

  Colour is token-only (--app-* / --tbl-*), so dark mode needs no companion
  block here.
-->
<template>
  <v-container fluid class="rd-page">
    <div v-if="canRead" class="rd-wrap">
      <v-card class="rd-card">
        <!-- Identity + primary action -->
        <div class="rd-head">
          <button class="rd-back" title="Back" @click="goToBack">
            <v-icon size="20">mdi-arrow-left</v-icon>
          </button>

          <div class="rd-head__identity">
            <h1 class="rd-head__title">{{ recordTitle }}</h1>
            <div class="rd-head__meta">
              <span class="rd-chip">{{ model.name.singular }}</span>
              <span class="rd-chip rd-chip--muted">ID {{ modelId }}</span>
              <span v-if="recordUid" class="rd-chip rd-chip--muted">{{
                recordUid
              }}</span>
            </div>
          </div>

          <v-btn
            v-if="canEdit"
            class="rd-edit"
            depressed
            :disabled="!record"
            @click="openEdit"
          >
            <v-icon size="16" left>mdi-pencil-outline</v-icon>
            Edit
          </v-btn>
        </div>

        <AError :api="api" />

        <!-- Tab strip -->
        <div class="rd-tabs" role="tablist">
          <button
            v-for="tabItem in tabs"
            :key="tabItem.key"
            class="rd-tab"
            :class="{ 'rd-tab--active': tabItem.key === currentTab }"
            role="tab"
            :aria-selected="tabItem.key === currentTab ? 'true' : 'false'"
            @click="currentTab = tabItem.key"
          >
            {{ tabLabel(tabItem) }}
          </button>
        </div>

        <!-- Only the active tab is mounted; see the header note. The `key` is
             what makes a tab switch remount the child table rather than reuse
             the previous relation's component instance. -->
        <div v-if="currentTabItem" class="rd-panel">
          <component
            v-if="currentTabItem.component"
            :is="currentTabItem.component"
            :key="currentTabItem.key"
          ></component>

          <div v-else-if="currentTabItem.key === 'info'" class="rd-info">
            <ALoader v-if="api.isLoading" :isLoading="api.isLoading" />
            <BreadDataRecordView
              v-else
              :record="record"
              :headers="model.read.table.headers"
            />
          </div>

          <BreadDataTableTabChild
            v-else-if="record && currentTabItem.dataType === 'model'"
            :key="currentTabItem.key"
            :tabProperties="currentTabItem"
            :parent="record"
            :parentModel="model"
            :childModelKey="currentTabItem.modelKey"
            :relationship="currentTabItem.relationship"
            :isTeamAdmin="isTeamAdmin"
            @total="setTabCount(currentTabItem.key, $event)"
            @changed="api.fetch()"
          />

          <!-- A relation tab can't render until the parent record has landed:
               its browse URL is keyed on a column of that record. -->
          <div v-else class="rd-empty">Loading {{ currentTabItem.label }}…</div>
        </div>
      </v-card>

      <!-- Edit happens over the record, not on a page of its own. The form is
           the SAME BreadModelForm the /item/edit route renders and posts to the
           same URL; only the chrome around it belongs to the dialog. `v-if`
           rather than v-show so each open starts from a clean form — otherwise
           a cancelled edit's values and validation state survive to the next. -->
      <v-dialog
        v-model="editOpen"
        max-width="680"
        :persistent="apiUpdate.isLoading"
        content-class="rd-dialog"
        @click:outside="closeEdit"
      >
        <div v-if="editOpen" class="rd-modal">
          <div class="rd-modal__head">
            <h2 class="rd-modal__title">Edit {{ model.name.singular }}</h2>
            <button class="rd-modal__close" title="Close" @click="closeEdit">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>

          <div class="rd-modal__body">
            <BreadModelForm
              ref="editForm"
              embedded
              action="edit"
              :api="apiUpdate"
              :model="model"
            />
          </div>

          <div class="rd-modal__foot">
            <button
              class="rd-modal__btn rd-modal__btn--ghost"
              type="button"
              @click="closeEdit"
            >
              Cancel
            </button>
            <v-btn
              class="rd-modal__btn rd-modal__btn--primary"
              depressed
              :loading="apiUpdate.isLoading"
              @click="submitEdit"
            >
              Save changes
            </v-btn>
          </div>
        </div>
      </v-dialog>
    </div>

    <v-card v-else class="rd-card">
      <div class="rd-denied">
        <v-icon size="20" color="var(--app-danger)" class="mr-2"
          >mdi-alert-circle-outline</v-icon
        >
        You don't have permission to read this model.
      </div>
    </v-card>
  </v-container>
</template>

<script>
import Api from "@/objects/api";
import BreadcrumbTabulator from "@/objects/breadcrumbTabulator";
import Model from "@/objects/model";
import Service from "@/objects/service";
import { mapState } from "vuex";

export default {
  computed: {
    ...mapState({
      services: (state) => state.service.data.services,
      rolePermission: (state) => state.rolePermission.data,
      auth: (state) => state.auth.data,
    }),
    /**
     * Looked up defensively rather than indexed straight into — a model whose
     * permission key is missing from the store used to take the whole page down
     * with a render error instead of showing the "no permission" card.
     */
    canRead() {
      return this.hasPermission(this.model.permissionKeys.read);
    },
    canEdit() {
      return (
        Boolean(this.model.edit) && this.hasPermission(this.model.permissionKeys.edit)
      );
    },
    recordTitle() {
      if (!this.record) return `${this.model.name.singular} ${this.modelId}`;
      if (this.model.column && this.model.column.text) {
        return this.record[this.model.column.text] || `${this.model.name.singular} ${this.modelId}`;
      }
      return this.record.name || `${this.model.name.singular} ${this.modelId}`;
    },
    currentTabItem() {
      return this.tabs.find((t) => t.key === this.currentTab) || null;
    },
    /** The model's own unique column (uuid, slug, …) when it isn't just `id`. */
    recordUid() {
      const uid = this.model.column && this.model.column.uid;
      if (!uid || !this.record) return null;
      return this.record[uid] || null;
    },
  },
  data: () => ({
    api: new Api(),
    apiUpdate: new Api(),
    ssoTeamApi: new Api(),
    breadcrumbTabulator: new BreadcrumbTabulator(),
    model: new Model(),
    modelId: null,
    service: new Service(),
    record: null,
    tabs: [],
    currentTab: "info",
    apiReadUrl: "",
    apiUpdateUrl: "",
    isTeamAdmin: false,
    editOpen: false,
    /** Live totals reported by the relation tabs, keyed by tab. */
    tabCounts: {},
  }),
  created() {
    this.model.getByKey(this.$route.params.modelKey);
    this.modelId = this.$route.params.modelId;
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Info leads: the record is the subject of a read page, its relations are
    // context. The old page pushed it on last, so opening a user landed on
    // "Roles" and the user's own details took a second click.
    const declaredTabs = this.model.read.tabs
      ? this.$_.cloneDeep(this.model.read.tabs)
      : [];
    this.tabs = [{ key: "info", label: "Info" }].concat(declaredTabs);
    this.currentTab = "info";

    /**
     * Keyed off the tab LIST, not `tabs[0]`. The old page tested only the first
     * tab, which is exactly the assumption reordering the strip would have
     * broken.
     */
    if (declaredTabs.some((t) => t.key === "ssoTeamToInternalUser")) {
      const ssoTeamId = this.$route.params.modelId;
      const internalUserUuid = this.auth.uuid;
      this.ssoTeamApi.setMethod("GET");
      this.ssoTeamApi.setUrl(
        `${this.$service["sso"]}/v1/en/console/ssoTeamToInternalUser/${ssoTeamId}/${internalUserUuid}`
      );
      this.ssoTeamApi.setCallbackCompleted((resp) => {
        this.isTeamAdmin = Boolean(resp.data[0] && resp.data[0].isAdmin);
      });
      this.ssoTeamApi.fetch();
    }

    this.breadcrumbTabulator.addForOperationRead({
      services: this.services,
      model: this.model,
      modelId: this.$route.params.modelId,
    });

    if (this.model.apiUrls.apiReadUrl != "") {
      this.apiReadUrl = `${this.$service[this.service.key]}${
        this.model.apiUrls.apiReadUrl
      }/${this.modelId}/read`;
    } else {
      this.apiReadUrl = `${
        this.$service[this.service.key]
      }/v1/en/console/tabulator/model/${this.model.key}/${this.modelId}/read`;
    }

    // Same URL the /item/edit page posts to, built the same way — the dialog
    // changes where the form lives, not what it talks to.
    if (this.model.apiUrls.apiUpdateUrl != "") {
      this.apiUpdateUrl = `${this.$service[this.service.key]}${
        this.model.apiUrls.apiUpdateUrl
      }/${this.modelId}/edit`;
    } else {
      this.apiUpdateUrl = `${
        this.$service[this.service.key]
      }/v1/en/console/tabulator/model/${this.model.key}/${this.modelId}/edit`;
    }

    this.apiUpdate.setMethod(`POST`);
    this.apiUpdate.setUrl(this.apiUpdateUrl);
    this.apiUpdate.setCallbackCompleted(() => {
      this.editOpen = false;
      // Re-read rather than merging the form buffer in: the API may normalise
      // what it stored, and `timestampUpdated` is on the Info tab.
      this.api.fetch();
      this.$store.dispatch("showMessage", {
        message: "Updated successfully.",
        messageType: "success",
        timeout: 2000,
      });
    });

    this.api.setUrl(this.apiReadUrl);
    this.api.setCallbackCompleted((resp) => {
      // AError and the old BreadTabulatorDataView both read `api.data[0]`, so
      // the wrapping array stays even though this page reads `record`.
      this.api.setData([resp]);
      this.record = resp;
    });
    this.api.fetch();
  },
  methods: {
    hasPermission(key) {
      const permission = this.rolePermission[key];
      return Boolean(permission && permission.isAllowed);
    },
    goToBack() {
      this.$router.go(-1);
    },
    /**
     * `_count` comes off the record itself and is the authoritative number, so
     * it wins over the tab's own total — a search inside a tab narrows that
     * total, and the strip should not report "Roles (0)" because you typed
     * something. The tab total is the fallback for models the read endpoint
     * doesn't count.
     */
    tabCount(tabItem) {
      if (!tabItem.modelKey) return null;
      const counts = (this.record && this.record._count) || {};
      const declared = counts[this.$_ucfirst(tabItem.modelKey)];
      if (typeof declared === "number") return declared;
      const observed = this.tabCounts[tabItem.key];
      return typeof observed === "number" ? observed : null;
    },
    tabLabel(tabItem) {
      const count = this.tabCount(tabItem);
      return count === null ? tabItem.label : `${tabItem.label} (${count})`;
    },
    setTabCount(key, total) {
      this.$set(this.tabCounts, key, total);
    },
    openEdit() {
      if (!this.canEdit || !this.record) return;
      // Clear the previous attempt's error banner. NOT via callbackReset() —
      // despite the name that is Api's pre-flight hook and sets isLoading TRUE,
      // which would open the dialog already spinning and, through
      // `:persistent`, refusing to close.
      this.apiUpdate.isError = false;
      this.apiUpdate.error = null;
      this.editOpen = true;
      // The dialog's content — and so the form ref — does not exist until it
      // has rendered once.
      this.$nextTick(() => {
        if (this.$refs.editForm) {
          // A clone, so typing edits the buffer and not the record behind the
          // dialog; Cancel then really discards.
          this.$refs.editForm.setData(this.$_.cloneDeep(this.record));
        }
      });
    },
    closeEdit() {
      if (this.apiUpdate.isLoading) return;
      this.editOpen = false;
    },
    submitEdit() {
      if (this.$refs.editForm) this.$refs.editForm.validate();
    },
  },
};
</script>

<style scoped>
/* The one hardcoded colour on the page. #d8dce2 is the console's page ground,
   shared with the browse page and the developer layout, and it has no token —
   so it gets the same treatment they use: literal in light, --app-page in dark. */
.rd-page {
  background-color: #d8dce2;
  font-family: var(--tbl-font);
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
}

.dark-mode .rd-page {
  background-color: var(--app-page);
}

.rd-card {
  border-radius: 12px;
  overflow: hidden;
  background: var(--app-card) !important;
}

/* ── Header ─────────────────────────────────────────────────────────────── */
.rd-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 18px 14px;
  flex-wrap: wrap;
}

.rd-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 10px;
  border: 1px solid var(--app-border);
  /* Sits ON the card, so it raises off --app-surface rather than --app-page. */
  background: var(--app-surface);
  color: var(--app-text-muted);
  transition: all 0.15s ease;
}

.rd-back:hover {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
  color: var(--app-text);
}

.rd-head__identity {
  flex: 1;
  min-width: 0;
}

.rd-head__title {
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: var(--app-text);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rd-head__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.rd-chip {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 9px;
  border-radius: 6px;
  background: var(--app-accent-soft);
  border: 1px solid var(--app-accent-border);
  color: var(--app-accent-strong);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rd-chip--muted {
  background: var(--app-surface);
  border-color: var(--app-border);
  color: var(--app-text-muted);
  font-weight: 600;
}

.rd-edit.v-btn {
  height: 38px;
  padding: 0 18px;
  border-radius: 10px;
  background: var(--app-accent) !important;
  color: var(--app-accent-fg) !important;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  box-shadow: none;
}

.rd-edit.v-btn:hover {
  background: var(--app-accent-strong) !important;
}

/* ── Tabs ───────────────────────────────────────────────────────────────── */
.rd-tabs {
  display: flex;
  gap: 4px;
  padding: 0 18px;
  border-bottom: 1px solid var(--app-border);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.rd-tab {
  position: relative;
  flex-shrink: 0;
  padding: 10px 14px;
  border: none;
  background: transparent;
  color: var(--app-text-muted);
  font-family: var(--tbl-font);
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
  transition: color 0.15s ease;
}

.rd-tab:hover {
  color: var(--app-text);
}

.rd-tab--active {
  color: var(--app-accent-strong);
}

/* The 2px underline sits ON the strip's own border, so the active tab reads as
   joined to the panel below it rather than as a floating pill. */
.rd-tab--active::after {
  content: "";
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: -1px;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: var(--app-accent);
}

.rd-panel {
  background: var(--app-page);
  min-height: 160px;
}

.rd-info {
  padding: 18px;
}

.rd-empty {
  padding: 40px 18px;
  text-align: center;
  color: var(--app-text-faint);
  font-size: 0.85rem;
  font-weight: 600;
}

/* ── Edit dialog ──────────────────────────────────────────────────────────
   Three tiers, and each one is painted rather than left to show whatever is
   behind it — a transparent band in a dialog shows the scrim, which on charcoal
   reads as a black hole punched through the panel.

     head / foot   --app-card      the dialog's own chrome
     body          --app-surface   the form area, one step back from the chrome
     fields        --app-field     the wells, one step forward of the form area

   The direction is what the token file asks for: a field reads as an input by
   being a step UP from what it sits on, so the form area has to sit BELOW the
   chrome for the wells to have somewhere to rise from. Before this the body was
   the card and the footer alone was --app-surface, so the only tonal step in the
   dialog was under the buttons — which is what made the footer look like a band
   stuck on rather than part of the panel. */
.rd-modal {
  background: var(--app-card);
  font-family: var(--tbl-font);
}

.rd-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

.rd-modal__title {
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
}

.rd-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--app-text-faint);
  transition: all 0.15s ease;
}

.rd-modal__close:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

/* Capped so a model with many fields scrolls the FORM rather than growing the
   dialog past the viewport and pushing its own Save button off-screen. The
   bottom is deeper than the top because a field's validation message renders
   below the last box and would otherwise sit on the footer's border. */
.rd-modal__body {
  padding: 22px 24px 26px;
  max-height: 60vh;
  overflow-y: auto;
  background: var(--app-surface);
}

.rd-modal__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--app-border);
  background: var(--app-card);
}

.rd-modal__btn {
  height: 38px;
  padding: 0 20px;
  border-radius: 10px;
  font-family: var(--tbl-font);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  transition: all 0.15s ease;
}

/* Transparent, not --app-card: the footer IS --app-card now, and a button the
   colour of the bar it stands on is a button you have to find by its border. */
.rd-modal__btn--ghost {
  border: 1px solid var(--app-border-strong);
  background: transparent;
  color: var(--app-text-muted);
}

.rd-modal__btn--ghost:hover {
  background: var(--app-hover);
  border-color: var(--app-border-strong);
  color: var(--app-text);
}

.rd-modal__btn--primary.v-btn {
  background: var(--app-accent) !important;
  color: var(--app-accent-fg) !important;
  box-shadow: none;
}

.rd-modal__btn--primary.v-btn:hover {
  background: var(--app-accent-strong) !important;
}

.rd-denied {
  display: flex;
  align-items: center;
  padding: 16px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--app-danger-fg);
  background: var(--app-danger-bg);
  border: 1px solid var(--app-danger-border);
  border-radius: 12px;
}

@media (max-width: 599px) {
  .rd-page {
    padding: 8px !important;
  }

  .rd-head {
    padding: 14px 12px 10px;
  }

  .rd-head__title {
    font-size: 1rem;
  }

  .rd-edit.v-btn {
    width: 100%;
  }

  .rd-tabs {
    padding: 0 10px;
  }

  .rd-info {
    padding: 12px 10px;
  }

  .rd-modal__head,
  .rd-modal__foot {
    padding: 12px;
  }

  .rd-modal__body {
    padding: 14px 12px;
    max-height: 66vh;
  }

  .rd-modal__btn {
    flex: 1;
  }
}
</style>

<style>
/* Unscoped: Vuetify moves the dialog's content element out of this component,
   and `content-class` lands on an element VDialog owns rather than on its root,
   so a scoped selector never reaches it. The panel inside it (.rd-modal) paints
   its own surface — this only shapes the container. */
.v-dialog.rd-dialog {
  border-radius: 14px;
  /* The container is painted too, so the panel's rounded corners cut into a
     surface rather than into the scrim. */
  background: var(--app-card);
  border: 1px solid var(--app-border);
  box-shadow: 0 24px 60px var(--app-shadow-deep, rgba(0, 0, 0, 0.35));
}

/* Vuetify paints every sheet its built-in near-black in the dark theme. The
   embedded form card is meant to be nothing but a wrapper inside this dialog,
   so it is pinned transparent here as well as in its own scoped block — an
   unscoped rule is what reaches it once VDialog has detached this content. */
.v-dialog.rd-dialog .v-card.bmf__card--embedded {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
